import { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { PageLoader } from '@/components/common/PageLoader';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { TopBar } from '@/components/layout/TopBar';
import { Sidebar } from '@/components/layout/Sidebar';
import { CoachmarkTour } from '@/components/tour';
import { AutomationProgressTracker } from '@/features/onboarding/components/AutomationProgressTracker';
import {
  useAuthStore,
  useOnboardingStore,
  useTourStore,
  useTooltipStore,
  usePlanStore,
} from '@/stores';
import { useSocialSocketEvents } from '@/hooks/useSocialSocketEvents';
import { useOnboardingStatusSync } from '@/hooks/useOnboardingStatusSync';
import { useSetupRevalidation } from '@/hooks/useSetupProgress';
import { usePlanSocketEvents } from '@/hooks/usePlanSocketEvents';
import { UpgradeListener, FeatureGate } from '@/components/billing';

const Dashboard = lazyWithRetry(() =>
  import('@/features/dashboard/pages/Dashboard').then(m => ({ default: m.Dashboard }))
);

const CalendarView = lazyWithRetry(() =>
  import('@/features/content/pages/CalendarView').then(m => ({ default: m.CalendarView }))
);

const CreatePostPage = lazyWithRetry(() =>
  import('@/features/content/pages/CreatePostPage').then(m => ({ default: m.CreatePostPage }))
);

const UserProfile = lazyWithRetry(() =>
  import('@/features/settings/pages/UserProfile').then(m => ({ default: m.UserProfile }))
);

const KnowledgeBase = lazyWithRetry(() =>
  import('@/features/discovery').then(m => ({ default: m.KnowledgeBase }))
);

const MarketNews = lazyWithRetry(() =>
  import('@/features/discovery').then(m => ({ default: m.MarketNews }))
);

const SocialListening = lazyWithRetry(() =>
  import('@/features/discovery').then(m => ({ default: m.SocialListening }))
);

const BillingUsage = lazyWithRetry(() =>
  import('@/features/settings').then(m => ({ default: m.BillingUsage }))
);

const Preferences = lazyWithRetry(() =>
  import('@/features/settings').then(m => ({ default: m.Preferences }))
);

const ContentReview = lazyWithRetry(() =>
  import('@/features/content/pages/ContentReview').then(m => ({ default: m.ContentReview }))
);

const ChatPage = lazyWithRetry(() =>
  import('@/features/chat').then(m => ({ default: m.ChatPage }))
);

const Channels = lazyWithRetry(() =>
  import('@/features/channels/pages/Channels').then(m => ({ default: m.Channels }))
);

const CompetitorListPage = lazyWithRetry(() =>
  import('@/features/competitors').then(m => ({ default: m.CompetitorListPage }))
);

const CompetitorFeedPage = lazyWithRetry(() =>
  import('@/features/competitors').then(m => ({ default: m.CompetitorFeedPage }))
);

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasCompletedOnboarding, hasSkippedOnboarding } = useAuthStore();
  const automationRunning = useOnboardingStore(s => s.automation.isRunning);
  useSocialSocketEvents();
  // An LTD purchase announced over the socket unlocks the nav without a reload.
  usePlanSocketEvents();
  // Reconcile the persisted onboarding flags with the backend on every entry
  // into the app shell — if automation is still running server-side this flips
  // the app into setup-pending mode and seeds the tracker below (LIL-440).
  useOnboardingStatusSync();
  // Keeps the setup checklist's five signals in step with the backend — mount
  // once, here, so every page shares one revalidation loop (LIL-467).
  useSetupRevalidation();

  // Hydrate billing data once so the sidebar plan badge + usage meter (LIL-341)
  // have data app-wide, independent of whether the user visits the billing page.
  const hydratePlan = usePlanStore(s => s.hydrate);
  const planHydrated = usePlanStore(s => s.hydrated);
  useEffect(() => {
    if (!planHydrated) void hydratePlan();
  }, [planHydrated, hydratePlan]);
  const pathname = location.pathname;
  const currentPage =
    pathname === '/app' || pathname === '/app/'
      ? 'dashboard'
      : pathname.replace(/^\/app\/?/, '') || 'dashboard';

  // Kick off the feature tour only on each user's FIRST visit to /app (LIL-355).
  // `autoStart` is a no-op for a user already in the persisted `seenUserIds`;
  // the TopBar "Restart product tour" button still re-runs it via reset().
  // ProtectedRoute syncs `sessionUserId` before this shell renders.
  // The tour itself handles mobile/tablet layout (responsive popover width,
  // viewport-aware placement, auto-opening the sidebar drawer for sidebar steps)
  // so we don't gate on viewport width.
  const sessionUserId = useAuthStore(s => s.sessionUserId);
  const tourSeen = useTourStore(s => !!sessionUserId && s.seenUserIds.includes(sessionUserId));
  const autoStartTour = useTourStore(s => s.autoStart);
  useEffect(() => {
    if (!sessionUserId || tourSeen) return;
    const t = window.setTimeout(() => autoStartTour(sessionUserId), 800);
    return () => window.clearTimeout(t);
  }, [sessionUserId, tourSeen, autoStartTour]);

  // Descriptive sidebar nav hover tooltips are an opt-in affordance, off by
  // default and toggled via the "Show tips" pill in the TopBar (LIL-377).
  const introTooltipsEnabled = useTooltipStore(s => s.tipsEnabled);

  return (
    <div className="flex h-screen supports-[height:100dvh]:h-dvh overflow-hidden">
      {(hasSkippedOnboarding || automationRunning) && !hasCompletedOnboarding && (
        <AutomationProgressTracker />
      )}
      <Sidebar
        onCreatePost={() => navigate('/app/create-post')}
        currentPage={currentPage}
        onNavigate={page => navigate(`/app/${page}`)}
        introTooltipsEnabled={introTooltipsEnabled}
      />

      <main className="flex-1 flex flex-col overflow-hidden">
        <TopBar
          onNavigateToProfile={() => navigate('/app/profile')}
          onNavigateToBilling={() => navigate('/app/billing')}
        />

        <div className="flex-1 overflow-y-auto min-h-0">
          <ErrorBoundary>
            <Suspense fallback={<PageLoader fullScreen={false} />}>
              <Routes>
                <Route path="dashboard" element={<Dashboard />} />
                <Route
                  path="calendar"
                  element={
                    <FeatureGate page="calendar">
                      <CalendarView />
                    </FeatureGate>
                  }
                />
                <Route
                  path="create-post"
                  element={
                    <FeatureGate page="create-post">
                      <CreatePostPage />
                    </FeatureGate>
                  }
                />
                <Route path="profile" element={<UserProfile />} />
                <Route
                  path="knowledge-base"
                  element={
                    <FeatureGate page="knowledge-base">
                      <KnowledgeBase />
                    </FeatureGate>
                  }
                />
                <Route
                  path="market-news"
                  element={
                    <FeatureGate page="market-news">
                      <MarketNews />
                    </FeatureGate>
                  }
                />
                <Route
                  path="topic-trends"
                  element={
                    <FeatureGate page="topic-trends">
                      <SocialListening />
                    </FeatureGate>
                  }
                />
                <Route
                  path="competitors"
                  element={
                    <FeatureGate page="competitors">
                      <CompetitorListPage />
                    </FeatureGate>
                  }
                />
                <Route
                  path="competitors/:id"
                  element={
                    <FeatureGate page="competitors">
                      <CompetitorFeedPage />
                    </FeatureGate>
                  }
                />
                <Route path="account-manager" element={<Navigate to="/app/chat" replace />} />
                <Route path="billing" element={<BillingUsage />} />
                <Route
                  path="preferences"
                  element={
                    <FeatureGate page="preferences">
                      <Preferences />
                    </FeatureGate>
                  }
                />
                <Route
                  path="content-review"
                  element={
                    <FeatureGate page="content-review">
                      <ContentReview />
                    </FeatureGate>
                  }
                />
                <Route
                  path="channels"
                  element={
                    <FeatureGate page="channels">
                      <Channels />
                    </FeatureGate>
                  }
                />
                <Route
                  path="chat/:conversationId?"
                  element={
                    <FeatureGate page="chat">
                      <ChatPage />
                    </FeatureGate>
                  }
                />

                <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </div>
      </main>
      <CoachmarkTour />
      <UpgradeListener />
    </div>
  );
}
